import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Check, X, Loader2, ExternalLink, Clock, Ban, Eye, Send, CheckCircle2, RotateCcw, Wallet, Trash2 } from "lucide-react";
import RecipePreview from "@/components/RecipePreview";

const PAYOUT = {
  pending: { label: "Payment pending", cls: "bg-amber-100 text-amber-700", icon: Clock },
  sent: { label: "Payment sent", cls: "bg-sky/15 text-sky", icon: Send },
  completed: { label: "Payment completed", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString() : ""; } catch { return ""; } };

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "deleted", label: "Deleted" },
];

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/submissions?status=${tab}`);
      setSubs(data.submissions || []);
      setCounts(data.counts || {});
    } catch { toast.error("Could not load submissions"); } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/submissions/${id}/approve`, { origin_url: window.location.origin });
      toast.success(data.emailed ? "Approved, published & creator emailed" : "Approved & published to the feed");
      await load();
      if (data.recipe_id) setTimeout(() => {}, 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); } finally { setBusy(null); }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/submissions/${id}/reject`);
      toast.success("Submission rejected");
      await load();
    } catch { toast.error("Reject failed"); } finally { setBusy(null); }
  };

  const updatePayout = async (id, status) => {
    setBusy(`payout-${id}`);
    try {
      await api.post(`/admin/submissions/${id}/payout`, { status });
      toast.success(status === "sent" ? "Marked as sent" : status === "completed" ? "Payment completed" : "Reset to pending");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update payment"); } finally { setBusy(null); }
  };

  const unpublish = async (id) => {
    setBusy(`del-${id}`);
    try {
      await api.post(`/admin/submissions/${id}/unpublish`);
      toast.success("Recipe removed from the feed");
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove recipe"); } finally { setBusy(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 animate-fade-up">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-1">Reel submissions</h1>
        <p className="text-nerd-light/55">Review creator submissions and approve the best to feature on the feed.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-nerd-light text-white" : "bg-black/5 text-nerd-light/70 hover:bg-black/10"}`}>
            {t.key === "pending" && <Clock className="w-3.5 h-3.5" />}
            {t.key === "approved" && <Check className="w-3.5 h-3.5" />}
            {t.key === "rejected" && <Ban className="w-3.5 h-3.5" />}
            {t.key === "deleted" && <Trash2 className="w-3.5 h-3.5" />}
            {t.label}
            <span className={`text-xs px-1.5 rounded-full ${tab === t.key ? "bg-white/20" : "bg-black/10"}`}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-nerd-light/40" /></div>
      ) : subs.length === 0 ? (
        <div className="py-24 text-center text-nerd-light/40 text-sm border border-dashed border-black/10 rounded-2xl">No {tab} submissions.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-submissions">
          {subs.map((s) => (
            <div key={s.id} data-testid={`submission-${s.id}`} className="bg-nerd-card border border-black/8 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-extrabold tracking-tight">{s.title}</h3>
                    <a href={s.reel_url} target="_blank" rel="noreferrer" data-testid={`sub-link-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-nerd-light/60 hover:text-nerd-light border border-black/10 rounded-full px-2 py-0.5">
                      <Instagram className="w-3 h-3" /> Reel <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-sm text-nerd-light/70 mt-1">
                    {s.creator_name}{s.instagram_handle ? ` · ${s.instagram_handle}` : ""} · <span className="text-nerd-light/45">{s.email}</span>
                  </p>
                  {s.description && <p className="text-sm text-nerd-light/60 mt-2 line-clamp-3">{s.description}</p>}
                  {s.status === "approved" && s.published_recipe_id && (
                    <button data-testid={`view-published-${s.id}`} onClick={() => navigate(`/recipe/${s.published_recipe_id}`)}
                      className="mt-2 text-xs font-semibold text-nerd-light underline">View published recipe →</button>
                  )}
                </div>
                {s.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`preview-${s.id}`} onClick={() => setPreview(s)} disabled={!s.recipe}
                      className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-4 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-40">
                      <Eye className="w-4 h-4" /> Preview
                    </button>
                    <button data-testid={`approve-${s.id}`} onClick={() => approve(s.id)} disabled={busy === s.id}
                      className="inline-flex items-center gap-1.5 bg-nerd-light text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60">
                      {busy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
                    </button>
                    <button data-testid={`reject-${s.id}`} onClick={() => reject(s.id)} disabled={busy === s.id}
                      className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-4 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-60">
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                )}
                {s.status !== "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`preview-${s.id}`} onClick={() => setPreview(s)} disabled={!s.recipe}
                      className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-4 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-40">
                      <Eye className="w-4 h-4" /> Preview
                    </button>
                    {s.status === "approved" && (
                      confirmDelete === s.id ? (
                        <>
                          <button data-testid={`confirm-delete-${s.id}`} onClick={() => unpublish(s.id)} disabled={busy === `del-${s.id}`}
                            className="inline-flex items-center gap-1.5 bg-red-500 text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60">
                            {busy === `del-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Confirm remove
                          </button>
                          <button data-testid={`cancel-delete-${s.id}`} onClick={() => setConfirmDelete(null)}
                            className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-3 py-2 rounded-full hover:bg-black/5 transition-colors">Cancel</button>
                        </>
                      ) : (
                        <button data-testid={`delete-recipe-${s.id}`} onClick={() => setConfirmDelete(s.id)} title="Remove from published feed"
                          className="inline-flex items-center gap-1.5 border border-red-200 text-red-500 text-sm font-semibold px-4 py-2 rounded-full hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" /> Delete recipe
                        </button>
                      )
                    )}
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${s.status === "approved" ? "bg-black/5 text-nerd-light" : "bg-black/5 text-nerd-light/50"}`}>
                      {s.status === "approved" ? "Approved" : s.status === "deleted" ? "Deleted" : "Rejected"}
                    </span>
                  </div>
                )}
              </div>

              {s.status === "approved" && (() => {
                const p = s.payout || { status: "pending", amount: 8.99, currency: "EUR", paypal_email: s.email };
                const meta = PAYOUT[p.status] || PAYOUT.pending;
                const PIcon = meta.icon;
                const payBusy = busy === `payout-${s.id}`;
                const amount = `€${Number(p.amount ?? 8.99).toFixed(2)}`;
                return (
                  <div data-testid={`payout-${s.id}`} className="mt-4 pt-4 border-t border-black/8 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-nerd-light">
                          <Wallet className="w-4 h-4 text-nerd-light/50" /> Creator payout {amount}
                        </span>
                        <span data-testid={`payout-status-${s.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                          <PIcon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <p className="text-xs text-nerd-light/50 mt-1">
                        PayPal: <span className="text-nerd-light/70">{p.paypal_email || s.email || "—"}</span>
                        {p.sent_at && ` · sent ${fmtDate(p.sent_at)}`}
                        {p.completed_at && ` · completed ${fmtDate(p.completed_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.status === "pending" && (
                        <button data-testid={`mark-sent-${s.id}`} onClick={() => updatePayout(s.id, "sent")} disabled={payBusy}
                          className="inline-flex items-center gap-1.5 bg-nerd-light text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60">
                          {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Mark as sent
                        </button>
                      )}
                      {p.status === "sent" && (
                        <>
                          <button data-testid={`mark-completed-${s.id}`} onClick={() => updatePayout(s.id, "completed")} disabled={payBusy}
                            className="inline-flex items-center gap-1.5 bg-nerd-light text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60">
                            {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark completed
                          </button>
                          <button data-testid={`payout-reset-${s.id}`} onClick={() => updatePayout(s.id, "pending")} disabled={payBusy} title="Reset to pending"
                            className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-3 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-60">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {p.status === "completed" && (
                        <button data-testid={`payout-reopen-${s.id}`} onClick={() => updatePayout(s.id, "sent")} disabled={payBusy} title="Reopen payment"
                          className="inline-flex items-center gap-1.5 border border-black/12 text-sm font-semibold px-3 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-60">
                          <RotateCcw className="w-4 h-4" /> Reopen
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div data-testid="admin-preview-overlay" className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-0 sm:p-6 overflow-y-auto">
          <div className="bg-white w-full max-w-6xl rounded-none sm:rounded-3xl min-h-full sm:min-h-0 my-0 sm:my-6">
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-8 py-4 bg-white/90 backdrop-blur border-b border-black/8 rounded-t-3xl">
              <div className="min-w-0">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-nerd-light/40">Recipe preview · as users see it</p>
                <p className="font-display font-bold truncate">{preview.creator_name}{preview.instagram_handle ? ` · ${preview.instagram_handle}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {preview.status === "pending" && (
                  <button data-testid="preview-approve-btn" onClick={() => { const id = preview.id; setPreview(null); approve(id); }}
                    className="inline-flex items-center gap-1.5 bg-nerd-light text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90">
                    <Check className="w-4 h-4" /> Approve
                  </button>
                )}
                <button data-testid="close-preview-btn" onClick={() => setPreview(null)}
                  className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="px-4 sm:px-8 py-6 sm:py-8">
              <RecipePreview
                recipe={preview.recipe}
                videoUrl={preview.video_url}
                title={preview.title}
                description={preview.description}
                creatorName={preview.creator_name}
                creatorHandle={preview.instagram_handle}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
